import React, { memo, useMemo } from 'react';
import { createUseStyles, Text, Icon, Popover, Advice, useTheme } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';
import { useWeb5Context } from "../../../../../lib/Web5Context";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'WidgetFooter');
const Footer = /*#__PURE__*/memo(_ref => {
  let {
    widgetFilters,
    dashboardFilters,
    forceBIUtils,
    customDashboardFilters
  } = _ref;
  const classes = useStyles();
  const {
    t,
    getWeb4EntityFromString
  } = useWeb5Context();
  const theme = useTheme();
  const finalFilters = useMemo(() => {
    const {
      getUniqueFilters
    } = forceBIUtils;
    const wFilters = getUniqueFilters(widgetFilters);
    const dFilters = getUniqueFilters(dashboardFilters);
    const finalWFilters = wFilters.uiFilters.reduce((obj, current) => {
      if (!obj.applied) obj.applied = [];
      if (!obj.notApplied) obj.notApplied = [];
      if (!dFilters.uiFilterNames.includes(current.fullId)) obj.applied.push(current);else obj.notApplied.push(current);
      return obj;
    }, {});
    return {
      wFilters: finalWFilters || {},
      dFilters: dFilters.uiFilters || []
    };
  }, [forceBIUtils, widgetFilters, dashboardFilters]);
  const widgetFiltersPopover = useMemo(() => {
    const {
      wFilters = {}
    } = finalFilters;
    const {
      applied = [],
      notApplied = []
    } = wFilters;
    return /*#__PURE__*/React.createElement("div", {
      className: classes.filtersPopover
    }, applied.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      className: classes.filtersPopoverItem,
      type: "subtitle",
      bold: true
    }, t('label_applied_filters')), applied.map((current, index) => {
      return /*#__PURE__*/React.createElement(Text, {
        key: index,
        className: classes.filtersPopoverItem,
        color: theme.colors.textLight.secondary
      }, `${current.description} (${t(current.entityLabel)})`);
    })), notApplied.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, applied.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: classes.filtersPopoverDivider
    }), /*#__PURE__*/React.createElement(Text, {
      className: classes.filtersPopoverItem,
      type: "subtitle",
      bold: true
    }, t('label_not_applied_filters')), /*#__PURE__*/React.createElement(Advice, {
      type: "info",
      showIcon: true,
      defaultCollapsed: false
    }, t('label_not_applied_filters_description')), notApplied.map((current, index) => {
      return /*#__PURE__*/React.createElement(Text, {
        key: index,
        className: classes.filtersPopoverItem,
        color: theme.colors.textLight.secondary
      }, `${current.description} (${t(current.entityLabel)})`);
    })));
  }, [classes, finalFilters, t, theme]);
  const renderWidgetFilters = useMemo(() => {
    const {
      wFilters = {}
    } = finalFilters;
    const {
      applied = [],
      notApplied = []
    } = wFilters;
    const totalFilters = [...applied, ...notApplied];
    const hasFilters = totalFilters.length !== 0;
    const iconName = hasFilters ? 'filterListActive' : 'filterList';
    const content = /*#__PURE__*/React.createElement("div", {
      className: classnames(classes.filtersContent, {
        [classes.withFilters]: hasFilters
      })
    }, /*#__PURE__*/React.createElement(Icon, {
      name: iconName
    }), /*#__PURE__*/React.createElement(Text, {
      type: "caption"
    }, `${totalFilters.length} ${t('label_widget')}`));
    return /*#__PURE__*/React.createElement("div", {
      className: classes.filtersContainerRight
    }, totalFilters.length > 0 && /*#__PURE__*/React.createElement(Popover, {
      content: widgetFiltersPopover,
      trigger: ['hover']
    }, content), totalFilters.length === 0 && content);
  }, [classes.filtersContainerRight, classes.filtersContent, classes.withFilters, finalFilters, t, widgetFiltersPopover]);
  const renderCustomDashboardFilters = useMemo(() => {
    if (!customDashboardFilters || !Object.keys(customDashboardFilters).length) return null;
    return Object.entries(customDashboardFilters).reduce((arr, _ref2) => {
      let [entity, filters] = _ref2;
      if (!filters || !Object.entries(filters)) return arr;
      Object.entries(filters).forEach(_ref3 => {
        let [id, filter] = _ref3;
        const newEntity = getWeb4EntityFromString(entity);
        arr.push(/*#__PURE__*/React.createElement(Text, {
          key: id,
          className: classes.filtersPopoverItem,
          color: theme.colors.textLight.secondary
        }, `${t(newEntity.labels.singular)} ${id} (${t(newEntity.labels.plural)})`));
      });
      return arr;
    }, []);
  }, [customDashboardFilters, classes.filtersPopoverItem, getWeb4EntityFromString, t, theme]);
  const dashboardFiltersPopover = useMemo(() => {
    const {
      dFilters = []
    } = finalFilters;
    return /*#__PURE__*/React.createElement("div", {
      className: classes.filtersPopover
    }, /*#__PURE__*/React.createElement(Text, {
      className: classes.filtersPopoverItem,
      type: "subtitle",
      bold: true
    }, t('label_applied_filters')), renderCustomDashboardFilters, dFilters.map((current, index) => {
      return /*#__PURE__*/React.createElement(Text, {
        key: index,
        className: classes.filtersPopoverItem,
        color: theme.colors.textLight.secondary
      }, `${current.description} (${t(current.entityLabel)})`);
    }));
  }, [classes.filtersPopover, classes.filtersPopoverItem, finalFilters, t, renderCustomDashboardFilters, theme]);
  const totalCustomDashboardFilters = useMemo(() => {
    if (!customDashboardFilters || !Object.keys(customDashboardFilters).length) return 0;
    return Object.entries(customDashboardFilters).reduce((num, _ref4) => {
      let [entity, filters] = _ref4;
      if (!filters || !Object.entries(filters)) return 0;
      Object.entries(filters).forEach(_ref5 => {
        let [id, filter] = _ref5;
        num++;
      });
      return num;
    }, 0);
  }, [customDashboardFilters]);
  const renderDashboardFilters = useMemo(() => {
    const {
      dFilters = []
    } = finalFilters;
    const hasFilters = dFilters.length + totalCustomDashboardFilters !== 0;
    const iconName = hasFilters ? 'filterListActive' : 'filterList';
    const totalDashboardFilters = dFilters.length + totalCustomDashboardFilters;
    const content = /*#__PURE__*/React.createElement("div", {
      className: classnames(classes.filtersContent, {
        [classes.withFilters]: hasFilters
      })
    }, /*#__PURE__*/React.createElement(Icon, {
      name: iconName
    }), /*#__PURE__*/React.createElement(Text, {
      type: "caption"
    }, `${totalDashboardFilters} ${t('label_dashboard')}`));
    return /*#__PURE__*/React.createElement("div", {
      className: classes.filtersContainerLeft
    }, totalDashboardFilters > 0 && /*#__PURE__*/React.createElement(Popover, {
      content: dashboardFiltersPopover,
      trigger: ['hover']
    }, content), totalDashboardFilters === 0 && content);
  }, [classes.filtersContainerLeft, classes.filtersContent, classes.withFilters, finalFilters, t, dashboardFiltersPopover, totalCustomDashboardFilters]);
  const renderFilters = useMemo(() => {
    return /*#__PURE__*/React.createElement(React.Fragment, null, renderDashboardFilters, /*#__PURE__*/React.createElement("div", {
      className: classes.divider
    }), renderWidgetFilters);
  }, [renderWidgetFilters, renderDashboardFilters, classes.divider]);
  return /*#__PURE__*/React.createElement("div", {
    className: classes.root
  }, renderFilters);
});
export default Footer;